import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
    Box,
    Chip,
    Grid,
    IconButton,
    Paper,
    Stack,
    Typography,
    GlobalStyles,
} from "@mui/material";
import { Cell, Pie, PieChart, Tooltip as ReTooltip } from "recharts";

import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import SpaceDashboardOutlinedIcon from "@mui/icons-material/SpaceDashboardOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

import { useTheme, alpha } from "@mui/material/styles";
import CustomCard from "../../components/common/CustomCard";
import CustomHeader from "../../components/common/CustomHeader";
import CustomTable from "../../components/common/CustomTable";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import { recruiterDashboard } from "../../api/dashboardApi";

const TOKEN_LIMIT = 1000000;
const INTERVIEW_LIMIT = 50;
// Beyond this many rows a table stops being scannable — a carousel takes over.
const CAROUSEL_THRESHOLD = 6;

export const KPI_CONFIGS = [
    {
        key: "active_jobs",
        title: "Active Jobs",
        subtitle: "Live postings",
        accentColor: "primary",
        icon: WorkOutlineOutlinedIcon,
    },
    {
        key: "total_candidates",
        title: "Candidates",
        subtitle: "Total applied",
        accentColor: "info",
        icon: PeopleOutlinedIcon,
    },
    {
        key: "interviews_scheduled",
        title: "Scheduled",
        subtitle: "Pending interviews",
        accentColor: "warning",
        icon: EventAvailableIcon,
    },
    {
        key: "interviews_completed",
        title: "Completed",
        subtitle: "Done",
        accentColor: "success",
        icon: CheckCircleOutlinedIcon,
    },
];

const JOB_COLUMNS = [
    { field: "job_title", label: "Job Title" },
    { field: "applications", label: "Applied" },
    {
        field: "status",
        label: "Status",
        render: (val) => <StatusChip status={val} />,
    },
];

const RECRUITER_COLUMNS = [
    { field: "candidate_name", label: "Candidate" },
    { field: "jd_name", label: "JD" },
    {
        field: "interview_status",
        label: "Interview Status",
        render: (val) => <StatusChip status={val} />,
    },
];

// ─── Global keyframes (registered once, used across every animated element) ──
function DashboardMotionStyles() {
    return (
        <GlobalStyles
            styles={{
                "@keyframes fadeSlideUp": {
                    "0%": { opacity: 0, transform: "translateY(14px)" },
                    "100%": { opacity: 1, transform: "translateY(0)" },
                },
                "@keyframes railFill": {
                    "0%": { transform: "scaleX(0)" },
                    "100%": { transform: "scaleX(1)" },
                },
                "@keyframes shimmerSweep": {
                    "0%": { transform: "translateX(-120%)" },
                    "100%": { transform: "translateX(220%)" },
                },
                "@keyframes floatBlob": {
                    "0%, 100%": { transform: "translate(0px, 0px) scale(1)" },
                    "50%": { transform: "translate(12px, -10px) scale(1.05)" },
                },
                "@keyframes pulseDot": {
                    "0%": { boxShadow: "0 0 0 0 rgba(46, 204, 113, 0.55)" },
                    "70%": { boxShadow: "0 0 0 8px rgba(46, 204, 113, 0)" },
                    "100%": { boxShadow: "0 0 0 0 rgba(46, 204, 113, 0)" },
                },
            }}
        />
    );
}

// ─── Count-up hook for KPI numbers ────────────────────────────────────────────
function useCountUp(target, duration = 900) {
    const [value, setValue] = useState(0);

    useEffect(() => {
        const to = Number(target) || 0;
        let raf;
        let start;

        function step(ts) {
            if (!start) start = ts;
            const progress = Math.min((ts - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setValue(Math.round(to * eased));
            if (progress < 1) raf = requestAnimationFrame(step);
        }

        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [target, duration]);

    return value;
}

function KpiCard({ title, value, subtitle, accentColor = "primary", icon: Icon, index = 0 }) {
    const theme = useTheme();
    const resolvedAccent = theme.palette[accentColor]?.main || accentColor;
    const animatedValue = useCountUp(value ?? 0);

    return (
        <CustomCard
            accentColor={accentColor}
            sx={{
                minHeight: 120,
                width: "100%",
                flex: 1,
                overflow: "hidden",
                position: "relative",
                opacity: 0,
                animation: "fadeSlideUp 0.5s cubic-bezier(.2,.7,.3,1) forwards",
                animationDelay: `${index * 80}ms`,
                transition: "transform 0.25s ease, box-shadow 0.25s ease",
                "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: `0 10px 24px ${alpha(resolvedAccent, 0.18)}`,
                },
            }}
            contentPadding={true}
            headerPadding={false}
            border={true}
        >
            {/* Ambient corner glow */}
            <Box
                sx={{
                    position: "absolute",
                    top: -30,
                    right: -30,
                    width: 90,
                    height: 90,
                    borderRadius: "50%",
                    background: `radial-gradient(circle, ${alpha(resolvedAccent, 0.16)}, transparent 70%)`,
                    animation: "floatBlob 6s ease-in-out infinite",
                    pointerEvents: "none",
                }}
            />

            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ position: "relative" }}>
                <Box>
                    <Typography
                        sx={{
                            fontSize: 28,
                            fontWeight: 700,
                            color: resolvedAccent,
                            lineHeight: 1.2,
                            letterSpacing: "-0.02em",
                            fontVariantNumeric: "tabular-nums",
                        }}
                    >
                        {animatedValue}
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: 13,
                            fontWeight: 500,
                            color: theme.palette.text.secondary,
                            mt: 0.5,
                        }}
                    >
                        {title}
                    </Typography>

                    {subtitle && (
                        <Typography sx={{ fontSize: 12, color: theme.palette.text.disabled, mt: 0.5 }}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>

                {Icon && (
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: alpha(resolvedAccent, 0.12),
                            border: `1px solid ${alpha(resolvedAccent, 0.3)}`,
                            color: resolvedAccent,
                            flexShrink: 0,
                            transition: "transform 0.3s ease",
                            ".MuiPaper-root:hover &": { transform: "rotate(-8deg) scale(1.08)" },
                        }}
                    >
                        <Icon fontSize="small" />
                    </Box>
                )}
            </Stack>
        </CustomCard>
    );
}

// ─── Signature element: pipeline conversion rail ──────────────────────────────
// Visualises how volume narrows from postings → candidates → scheduled → done,
// which is the one real sequence in this data (a hiring funnel), animated as
// a set of proportionally-scaled bars rather than decorative numbering.
function PipelineRail({ kpis }) {
    const theme = useTheme();
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setMounted(true), 120);
        return () => clearTimeout(t);
    }, []);

    const stages = useMemo(() => {
        const raw = [
            { label: "Jobs Posted", value: kpis.active_jobs ?? 0, color: theme.palette.primary.main },
            { label: "Candidates", value: kpis.total_candidates ?? 0, color: theme.palette.info.main },
            { label: "Scheduled", value: kpis.interviews_scheduled ?? 0, color: theme.palette.warning.main },
            { label: "Completed", value: kpis.interviews_completed ?? 0, color: theme.palette.success.main },
        ];
        const max = Math.max(...raw.map((r) => r.value), 1);
        return raw.map((r) => ({ ...r, pct: Math.max((r.value / max) * 100, r.value > 0 ? 6 : 0) }));
    }, [kpis, theme]);

    return (
        <CustomCard
            headerPadding={false}
            contentPadding={true}
            sx={{
                mb: 2,
                opacity: 0,
                animation: "fadeSlideUp 0.5s cubic-bezier(.2,.7,.3,1) forwards",
                animationDelay: "40ms",
            }}
            border={true}
        >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <ArrowForwardIcon sx={{ fontSize: 16, color: theme.palette.text.disabled }} />
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: theme.palette.text.primary }}>
                    Hiring Pipeline Flow
                </Typography>
                <Typography sx={{ fontSize: 11, color: theme.palette.text.disabled }}>
                    · relative volume by stage
                </Typography>
            </Stack>

            <Stack spacing={1.1}>
                {stages.map((stage, i) => (
                    <Stack key={stage.label} direction="row" alignItems="center" spacing={1.5}>
                        <Typography
                            sx={{
                                fontSize: 11.5,
                                color: theme.palette.text.secondary,
                                width: 96,
                                flexShrink: 0,
                            }}
                        >
                            {stage.label}
                        </Typography>

                        <Box
                            sx={{
                                position: "relative",
                                flex: 1,
                                height: 10,
                                borderRadius: 999,
                                backgroundColor: alpha(theme.palette.text.primary, 0.06),
                                overflow: "hidden",
                            }}
                        >
                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    width: `${stage.pct}%`,
                                    borderRadius: 999,
                                    background: `linear-gradient(90deg, ${alpha(stage.color, 0.55)}, ${stage.color})`,
                                    transformOrigin: "left center",
                                    transform: mounted ? "scaleX(1)" : "scaleX(0)",
                                    transition: `transform 0.8s cubic-bezier(.2,.8,.3,1) ${i * 90}ms`,
                                }}
                            />
                        </Box>

                        <Typography
                            sx={{
                                fontSize: 12.5,
                                fontWeight: 700,
                                color: stage.color,
                                width: 34,
                                textAlign: "right",
                                flexShrink: 0,
                                fontVariantNumeric: "tabular-nums",
                            }}
                        >
                            {stage.value}
                        </Typography>
                    </Stack>
                ))}
            </Stack>
        </CustomCard>
    );
}

function SectionCard({ title, subtitle, accentColor = "primary", badge, children, sx }) {
    const theme = useTheme();
    const resolvedAccent = theme.palette[accentColor]?.main || accentColor;

    return (
        <CustomCard
            accentColor={accentColor}
            headerPadding={false}
            contentPadding={true}
            sx={{ flex: 1, minHeight: "100%", overflow: "hidden", ...sx }}
        >
            {(title || subtitle) && (
                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                            sx={{
                                width: 3,
                                height: 22,
                                borderRadius: "99px",
                                backgroundColor: resolvedAccent,
                                flexShrink: 0,
                            }}
                        />
                        <Box>
                            <Typography sx={{ fontSize: 16, fontWeight: 600, color: theme.palette.text.primary, lineHeight: 1.2 }}>
                                {title}
                            </Typography>
                            {subtitle && (
                                <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary, mt: 0.3 }}>
                                    {subtitle}
                                </Typography>
                            )}
                        </Box>
                    </Stack>

                    {badge && (
                        <Chip
                            label={badge}
                            size="small"
                            sx={{
                                fontSize: 11,
                                fontWeight: 600,
                                height: 22,
                                backgroundColor: alpha(resolvedAccent, 0.12),
                                color: resolvedAccent,
                                border: `1px solid ${alpha(resolvedAccent, 0.3)}`,
                            }}
                        />
                    )}
                </Stack>
            )}

            {children}
        </CustomCard>
    );
}

function UsageBar({ label, used = 0, limit = 1, color = "primary" }) {
    const theme = useTheme();
    const [mounted, setMounted] = useState(false);
    const pct = Math.min((used / limit) * 100, 100);
    const resolvedColor = theme.palette[color]?.main || color;

    useEffect(() => {
        const t = setTimeout(() => setMounted(true), 150);
        return () => clearTimeout(t);
    }, []);

    return (
        <Stack spacing={0.75}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontSize: 13, color: theme.palette.text.secondary, fontWeight: 500 }}>
                    {label}
                </Typography>
                <Typography sx={{ fontSize: 11, color: theme.palette.text.disabled }}>
                    {used.toLocaleString()} / {limit.toLocaleString()}
                </Typography>
            </Stack>

            <Box
                sx={{
                    height: 6,
                    borderRadius: 999,
                    backgroundColor: alpha(theme.palette.common.white, 0.08),
                    overflow: "hidden",
                    position: "relative",
                }}
            >
                <Box
                    sx={{
                        width: mounted ? `${pct}%` : 0,
                        height: "100%",
                        borderRadius: 999,
                        backgroundColor: resolvedColor,
                        transition: "width 0.9s cubic-bezier(.2,.8,.3,1)",
                        position: "relative",
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            width: "40%",
                            background: `linear-gradient(90deg, transparent, ${alpha("#fff", 0.45)}, transparent)`,
                            animation: "shimmerSweep 2.2s ease-in-out infinite",
                        }}
                    />
                </Box>
            </Box>

            <Typography sx={{ fontSize: 10, color: theme.palette.text.disabled }}>
                {Math.round(pct)}% used
            </Typography>
        </Stack>
    );
}

function DonutLegendRow({ label, value, color = "primary", divider = false }) {
    const theme = useTheme();
    const resolvedColor = theme.palette[color]?.main || color;

    return (
        <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            py={1.5}
            sx={{
                width: "100%",
                ...(divider && { borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}` }),
            }}
        >
            <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: resolvedColor, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 14, color: theme.palette.text.secondary, fontWeight: 500 }}>
                    {label}
                </Typography>
            </Stack>

            <Typography
                sx={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: resolvedColor,
                    minWidth: "24px",
                    textAlign: "right",
                    fontFamily: theme.typography.fontFamily,
                }}
            >
                {value}
            </Typography>
        </Stack>
    );
}

function MiniTokenCard({ value, label, color = "primary" }) {
    const theme = useTheme();
    const resolvedColor = theme.palette[color]?.main || color;

    return (
        <CustomCard sx={{ flex: 1, p: 2 }} contentPadding={false}>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: resolvedColor }}>{value}</Typography>
            <Typography
                sx={{
                    fontSize: 10,
                    color: theme.palette.text.disabled,
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    mt: 0.5,
                }}
            >
                {label}
            </Typography>
        </CustomCard>
    );
}

function StatusChip({ status = "Closed" }) {
    const theme = useTheme();

    const getStatusColor = () => {
        const s = String(status || "").toUpperCase();
        if (s === "OPEN" || s === "COMPLETED" || s === "SELECTED") return theme.palette.success.main;
        if (s === "IN PROGRESS" || s === "PENDING") return theme.palette.warning.main;
        if (s === "CLOSED" || s === "REJECTED" || s === "TERMINATED") return theme.palette.error.main;
        if (s === "ATTENDED" || s === "SCHEDULED") return theme.palette.info.main;
        return theme.palette.grey[500];
    };

    const color = getStatusColor();

    return (
        <Chip
            label={status}
            size="small"
            sx={{
                textTransform: "capitalize",
                backgroundColor: alpha(color, 0.12),
                color: color,
                border: `1px solid ${alpha(color, 0.4)}`,
                fontSize: 11,
                fontWeight: 500,
                height: 22,
                letterSpacing: "0.4px",
                borderRadius: "999px",
                "& .MuiChip-label": { px: 1.25 },
            }}
        />
    );
}

function DonutTooltip({ active, payload }) {
    const theme = useTheme();
    if (!active || !payload?.length) return null;

    return (
        <Paper
            elevation={0}
            sx={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                borderRadius: 2,
                px: 1.5,
                py: 1,
            }}
        >
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: theme.palette.text.primary }}>
                {payload[0].name}
            </Typography>
            <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary }}>
                {payload[0].value} interviews
            </Typography>
        </Paper>
    );
}

// ─── Generic slider used once a list exceeds the CAROUSEL_THRESHOLD ──────────
function DataCarousel({ items, renderItem, itemWidth = 260 }) {
    const theme = useTheme();
    const trackRef = useRef(null);
    const [index, setIndex] = useState(0);

    const scrollToIndex = useCallback((i) => {
        const clamped = Math.max(0, Math.min(i, items.length - 1));
        const el = trackRef.current;
        const child = el?.children?.[clamped];
        if (child) child.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
        setIndex(clamped);
    }, [items.length]);

    return (
        <Box sx={{ position: "relative", px: 4 }}>
            <IconButton
                size="small"
                onClick={() => scrollToIndex(index - 1)}
                disabled={index === 0}
                sx={{
                    position: "absolute",
                    left: -4,
                    top: "38%",
                    zIndex: 2,
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                    boxShadow: 2,
                    "&:hover": { backgroundColor: theme.palette.background.paper },
                }}
            >
                <ChevronLeftIcon fontSize="small" />
            </IconButton>

            <Stack
                ref={trackRef}
                direction="row"
                spacing={1.5}
                sx={{
                    overflowX: "auto",
                    scrollSnapType: "x mandatory",
                    pb: 1,
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                }}
            >
                {items.map((item, i) => (
                    <Box
                        key={i}
                        sx={{
                            scrollSnapAlign: "start",
                            flexShrink: 0,
                            minWidth: itemWidth,
                            opacity: 0,
                            animation: "fadeSlideUp 0.4s ease forwards",
                            animationDelay: `${Math.min(i, 8) * 45}ms`,
                        }}
                    >
                        {renderItem(item, i)}
                    </Box>
                ))}
            </Stack>

            <IconButton
                size="small"
                onClick={() => scrollToIndex(index + 1)}
                disabled={index === items.length - 1}
                sx={{
                    position: "absolute",
                    right: -4,
                    top: "38%",
                    zIndex: 2,
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                    boxShadow: 2,
                    "&:hover": { backgroundColor: theme.palette.background.paper },
                }}
            >
                <ChevronRightIcon fontSize="small" />
            </IconButton>

            <Stack direction="row" spacing={0.6} justifyContent="center" mt={1}>
                {items.map((_, i) => (
                    <Box
                        key={i}
                        onClick={() => scrollToIndex(i)}
                        sx={{
                            width: i === index ? 16 : 6,
                            height: 6,
                            borderRadius: 999,
                            backgroundColor: i === index ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.15),
                            transition: "all 0.25s ease",
                            cursor: "pointer",
                        }}
                    />
                ))}
            </Stack>
        </Box>
    );
}

function JobSlideCard({ job }) {
    const theme = useTheme();
    return (
        <Paper
            elevation={0}
            sx={{
                p: 1.75,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                backgroundColor: alpha(theme.palette.primary.main, 0.03),
                height: "100%",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": { transform: "translateY(-2px)", boxShadow: `0 8px 18px ${alpha(theme.palette.common.black, 0.12)}` },
            }}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: theme.palette.text.primary, lineHeight: 1.3 }}>
                    {job.job_title}
                </Typography>
                <StatusChip status={job.status} />
            </Stack>

            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1.5 }}>
                <PeopleOutlinedIcon sx={{ fontSize: 15, color: theme.palette.text.disabled }} />
                <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary }}>
                    {job.applications ?? 0} applied
                </Typography>
            </Stack>
        </Paper>
    );
}

function RecruiterSlideCard({ item }) {
    const theme = useTheme();
    return (
        <Paper
            elevation={0}
            sx={{
                p: 1.75,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                backgroundColor: alpha(theme.palette.info.main, 0.03),
                height: "100%",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": { transform: "translateY(-2px)", boxShadow: `0 8px 18px ${alpha(theme.palette.common.black, 0.12)}` },
            }}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: theme.palette.text.primary, lineHeight: 1.3 }}>
                    {item.candidate_name}
                </Typography>
                <StatusChip status={item.interview_status} />
            </Stack>

            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1.5 }}>
                <DescriptionOutlinedIcon sx={{ fontSize: 15, color: theme.palette.text.disabled }} />
                <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {item.jd_name}
                </Typography>
            </Stack>
        </Paper>
    );
}

function DashboardSkeleton() {
    return (
        <Box sx={{ p: 3 }}>
            <Grid container spacing={2} mb={2}>
                {[...Array(4)].map((_, i) => (
                    <Grid item xs={12} md={3} key={i}>
                        <SkeletonLoader type="card" count={1} />
                    </Grid>
                ))}
            </Grid>

            <Grid container spacing={2} mb={2}>
                {[6, 6].map((md, i) => (
                    <Grid item xs={12} md={md} key={i}>
                        <SkeletonLoader type="table" rows={5} columns={[1, 2, 3, 4, 5]} />
                    </Grid>
                ))}
            </Grid>

            <Grid container spacing={2}>
                {[6, 6].map((md, i) => (
                    <Grid item xs={12} md={md} key={i}>
                        <SkeletonLoader type="card" count={1} />
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function RecruiterDashboard() {
    const theme = useTheme();

    const [dashboardData, setDashboardData] = useState({
        kpis: {},
        interview_status: {},
        token_usage: {},
        jobs: [],
        recruiters: [],
    });

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [overviewRes, jobsRes, interviewRes] = await Promise.all([
                    recruiterDashboard.recruiterOverview(),
                    recruiterDashboard.recruiterJobs(),
                    recruiterDashboard.recruiterInterviews(),
                ]);

                setDashboardData({
                    kpis: overviewRes.kpis || {},
                    token_usage: overviewRes.token_usage || {},
                    interview_status: overviewRes.interview_status || {},
                    jobs: jobsRes || [],
                    recruiters: interviewRes || [],
                });
            } catch (error) {
                console.error("Dashboard fetch error", error);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const { kpis, interview_status, token_usage, jobs, recruiters } = dashboardData;

    const donutData = useMemo(
        () => [
            { name: "Completed", value: interview_status.completed ?? 0 },
            { name: "In Progress", value: interview_status.in_progress ?? 0 },
            { name: "Scheduled", value: interview_status.scheduled ?? 0 },
        ],
        [interview_status]
    );

    const totalInterviews = useMemo(() => donutData.reduce((acc, d) => acc + d.value, 0), [donutData]);

    const DONUT_COLORS = [theme.palette.success.main, theme.palette.warning.main, theme.palette.info.main];

    const jobsNeedCarousel = jobs.length > CAROUSEL_THRESHOLD;
    const recruitersNeedCarousel = recruiters.length > CAROUSEL_THRESHOLD;

    return (
        <Box sx={{ backgroundColor: theme.palette.background.default, minHeight: "100vh", position: "relative" }}>
            <DashboardMotionStyles />

            {/* Ambient background blobs — subtle, theme-aware, non-interactive */}
            <Box
                sx={{
                    position: "absolute",
                    top: 40,
                    right: 60,
                    width: 260,
                    height: 260,
                    borderRadius: "50%",
                    background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.06)}, transparent 70%)`,
                    animation: "floatBlob 10s ease-in-out infinite",
                    pointerEvents: "none",
                    zIndex: 0,
                }}
            />

            <CustomHeader
                title="Dashboard Overview"
                subtitle="Monitor your job postings, candidate pipeline, and AI usage metrics."
                icon={<SpaceDashboardOutlinedIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />}
                breadcrumbs={[{ label: "Dashboard", path: "/recruiter/dashboard" }]}
            />

            {loading ? (
                <DashboardSkeleton />
            ) : (
                <Box sx={{ p: 3, overflow: "auto", position: "relative", zIndex: 1 }}>
                    {/* ── KPI Row ── */}
                    <Grid container spacing={2} mb={2} alignItems="stretch">
                        {KPI_CONFIGS.map((item, i) => (
                            <Grid item xs={12} sm={6} md={3} key={item.key} sx={{ display: "flex", flexDirection: "column" }}>
                                <KpiCard {...item} value={kpis[item.key]} index={i} />
                            </Grid>
                        ))}
                    </Grid>

                    {/* ── Signature: Pipeline Conversion Rail ── */}
                    <PipelineRail kpis={kpis} />

                    {/* ── Tables / Carousels Row ── */}
                    <Grid container spacing={2} mb={2} alignItems="flex-start">
                        <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column" }}>
                            <SectionCard
                                title="Job Performance"
                                subtitle="Live overview of all postings"
                                accentColor="success"
                                badge={jobsNeedCarousel ? `${jobs.length} roles` : null}
                            >
                                {jobsNeedCarousel ? (
                                    <DataCarousel items={jobs} renderItem={(job) => <JobSlideCard job={job} />} />
                                ) : (
                                    <CustomTable columns={JOB_COLUMNS} rows={jobs} />
                                )}
                            </SectionCard>
                        </Grid>

                        <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column" }}>
                            <SectionCard
                                title="Recruiter Activity"
                                subtitle="Individual performance metrics"
                                accentColor="info"
                                badge={recruitersNeedCarousel ? `${recruiters.length} entries` : null}
                            >
                                {recruitersNeedCarousel ? (
                                    <DataCarousel items={recruiters} renderItem={(item) => <RecruiterSlideCard item={item} />} />
                                ) : (
                                    <CustomTable columns={RECRUITER_COLUMNS} rows={recruiters} />
                                )}
                            </SectionCard>
                        </Grid>
                    </Grid>

                    {/* ── Bottom Row ── */}
                    <Grid container spacing={2} alignItems="flex-start">
                        {/* Donut */}
                        <Grid item xs={12} md={6}>
                            <SectionCard title="Interview Status" subtitle="Distribution by stage" accentColor="warning" sx={{ height: "100%" }}>
                                <Stack direction="row" alignItems="center" spacing={4} sx={{ width: "100%", pr: 2 }}>
                                    <Box sx={{ position: "relative" }}>
                                        <PieChart width={180} height={180}>
                                            <Pie
                                                data={donutData}
                                                cx={90}
                                                cy={90}
                                                innerRadius={56}
                                                outerRadius={78}
                                                dataKey="value"
                                                strokeWidth={0}
                                                isAnimationActive
                                                animationDuration={900}
                                                animationEasing="ease-out"
                                            >
                                                {donutData.map((_, i) => (
                                                    <Cell key={i} fill={DONUT_COLORS[i]} />
                                                ))}
                                            </Pie>
                                            <ReTooltip content={<DonutTooltip />} />
                                        </PieChart>

                                        <Stack
                                            alignItems="center"
                                            justifyContent="center"
                                            sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}
                                        >
                                            <Typography sx={{ fontSize: 24, fontWeight: 800, color: theme.palette.text.primary }}>
                                                {totalInterviews}
                                            </Typography>
                                            <Typography sx={{ fontSize: 10, color: theme.palette.text.disabled, letterSpacing: "1px" }}>
                                                TOTAL
                                            </Typography>
                                        </Stack>
                                    </Box>

                                    <Box sx={{ flex: 1 }}>
                                        {donutData.map((entry, i) => (
                                            <DonutLegendRow
                                                key={entry.name}
                                                label={entry.name}
                                                value={entry.value}
                                                color={DONUT_COLORS[i]}
                                                divider={i < donutData.length - 1}
                                            />
                                        ))}
                                    </Box>
                                </Stack>
                            </SectionCard>
                        </Grid>

                        {/* Usage */}
                        {/* <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column" }}>
                            <SectionCard title="AI Usage" subtitle="Token consumption & credits" accentColor="error" sx={{ height: "100%" }}>
                                <Stack spacing={2.5}>
                                    <UsageBar label="AI Interviews" used={kpis.interviews_completed ?? 0} limit={INTERVIEW_LIMIT} color="success" />
                                    <UsageBar label="Total Tokens" used={token_usage.total_tokens ?? 0} limit={TOKEN_LIMIT} color="warning" />

                                    <Box sx={{ height: 1, backgroundColor: alpha(theme.palette.divider, 0.6) }} />

                                    <Stack direction="row" spacing={1.5}>
                                        <MiniTokenCard value={`${((token_usage.input_tokens ?? 0) / 1000).toFixed(1)}K`} label="Input" color="info" />
                                        <MiniTokenCard value={`${((token_usage.output_tokens ?? 0) / 1000).toFixed(1)}K`} label="Output" color="error" />
                                    </Stack>
                                </Stack>
                            </SectionCard>
                        </Grid> */}
                    </Grid>
                </Box>
            )}
        </Box>
    );
}